import { PriceData } from 'src/components/sections/NewPDP/typings'

type GenericObject = { [key: string]: any }
type Badge = { id: string | null; name: string | null } | null
type BadgesNullish = Badge[] | null
type OrderedBadge = Badge & { weight?: number }
type OrderedBadgeNullish = OrderedBadge | null

export const deepObjectCopy = (obj: GenericObject) => {
  return JSON.parse(JSON.stringify(obj))
}

const getTypeOfPage = (type: string) => {
  if (!type) {
    return ''
  }

  const categories: Record<string, string> = {
    Category: 'categoryId',
    Department: 'categoryId',
    SubCategory: 'categoryId',
    Brand: 'brandId',
    Collection: 'clusterId',
  }

  return categories[type] ?? ''
}

export const getPlpSections = ({
  allPlpNodes,
  collection: currentCollection,
}: GetPlpSectionsProps): Section[] => {
  const genericSections: Section[] = []
  const plpSections: Section[] = []

  const typeOfCurrentPage = getTypeOfPage(currentCollection?.type)

  for (const plpNode of allPlpNodes) {
    const parametersFromNode = plpNode?.node?.parameters?.collection

    if (!currentCollection?.id && !parametersFromNode?.generic) {
      continue
    }

    const isAValidSection =
      currentCollection?.id &&
      currentCollection?.id ===
        parametersFromNode?.[typeOfCurrentPage as keyof PlpCollection]

    if (isAValidSection) {
      plpSections.push(...plpNode.node.sections)
    }

    if (parametersFromNode?.generic) {
      genericSections.push(...plpNode?.node?.sections)
    }
  }

  return plpSections.length ? plpSections : genericSections
}

const getThirtyDaysPrice = (
  minPriceThirtyDays: string | null | undefined,
  skuId: string
) => {
  if (!minPriceThirtyDays) return {}

  try {
    const thirtyDaysPrice = JSON.parse(minPriceThirtyDays)
    if (thirtyDaysPrice) {
      const thirtyDaysPriceObject = thirtyDaysPrice[skuId]
      if (thirtyDaysPriceObject) {
        return {
          thirtyDaysPrice: thirtyDaysPriceObject.minPrice as number,
          showMinPrice: thirtyDaysPriceObject.showMinPrice as boolean,
        }
      }
      return {}
    }
    return {}
  } catch (e) {
    return {}
  }
}

export const getPriceInfo = (priceData: PriceData, isAlgolia = false) => {
  const { listPrice, lowPrice, unitMultiplier, minPriceThirtyDays, skuId } =
    priceData
  const verifiedPrice = lowPrice
  // const verifiedPrice = listPrice > lowPrice ? lowPrice : listPrice
  const { thirtyDaysPrice, showMinPrice } = isAlgolia
    ? JSON.parse(minPriceThirtyDays || '{}')
    : getThirtyDaysPrice(minPriceThirtyDays, skuId)
  let isDiscount = false

  if (lowPrice > 0) {
    // Out of stock products have a low price of 0 so we must check if the product is not out of stock
    if (thirtyDaysPrice && showMinPrice) {
      // If the product has more than 30 days of stock we calculate the discount percentage based on 30 days price
      isDiscount = verifiedPrice < thirtyDaysPrice
    } else if (thirtyDaysPrice && !showMinPrice) {
      // This is the case in which the product has less than 30 days of stock
      isDiscount = verifiedPrice < listPrice
    }
  }

  // We add the flat versions of the prices because in the minicart we must not multiply the price by the unit multiplier
  const verifiedPriceToShow = unitMultiplier
    ? verifiedPrice * unitMultiplier
    : verifiedPrice

  const verifiedPriceToShowFlat = verifiedPrice

  const verifiedListPriceToShow = unitMultiplier
    ? listPrice * unitMultiplier
    : listPrice

  const verifiedListPriceToShowFlat = listPrice

  const verifiedThirtyDaysPrice = thirtyDaysPrice
    ? unitMultiplier
      ? thirtyDaysPrice * unitMultiplier
      : thirtyDaysPrice
    : null

  const verifiedThirtyDaysPriceFlat = thirtyDaysPrice

  let discount = null
  let badgeToShow: null | 'miglior-prezzo' | 'prezzo-lancio' = null
  let mustShowDiscountPercentage = false

  if (isDiscount) {
    // If the product has more than 30 days of stock we calculate the discount percentage based on 30 days price
    if (showMinPrice && verifiedThirtyDaysPrice) {
      discount = Math.round(
        (1 - verifiedPriceToShow / verifiedThirtyDaysPrice) * 100
      )
      badgeToShow = 'miglior-prezzo'
      mustShowDiscountPercentage = true
    } else {
      // Otherwise we calculate the discount percentage based on the list price
      discount = Math.round(
        (1 - verifiedPriceToShow / verifiedListPriceToShow) * 100
      )
      badgeToShow = 'prezzo-lancio'
    }
  }

  const verifiedUnitMultiplier = unitMultiplier ?? 1

  return {
    verifiedPriceToShowFlat,
    verifiedPriceToShow,
    verifiedListPriceToShowFlat,
    verifiedListPriceToShow,
    isDiscount,
    verifiedUnitMultiplier,
    badgeToShow,
    mustShowDiscountPercentage,
    ...(isDiscount && discount && { discount }),
    ...(isDiscount && {
      verifiedOriginalPrice: showMinPrice
        ? verifiedThirtyDaysPrice
        : verifiedListPriceToShow,
      verifiedOriginalPriceFlat: showMinPrice
        ? verifiedThirtyDaysPriceFlat
        : verifiedListPriceToShowFlat,
    }),
  }
}

const weightProductBadges = (badges: BadgesNullish): OrderedBadgeNullish[] => {
  if (!badges || badges.length === 0) {
    return []
  }

  if (!Array.isArray(badges)) {
    badges = [badges]
  }

  const orderedBadges = badges.map((badge) => {
    if (!badge) return null

    const fixedName = badge?.name?.toLocaleLowerCase()
    if (fixedName) {
      if (
        fixedName.includes('miglior prezzo') ||
        fixedName.includes('prezzo lancio')
      ) {
        return { ...badge, weight: 1 }
      }
      if (fixedName.includes('selezione top')) {
        return { ...badge, weight: 2 }
      }
      if (fixedName.includes('scelta consigliata')) {
        return { ...badge, weight: 3 }
      }
      return { ...badge, weight: 99 }
    }
    return null
  })

  return orderedBadges
}

export const sortProductBadges = (badges: BadgesNullish) => {
  const weightedBadges = weightProductBadges(badges)
  if (!weightedBadges || weightedBadges.length === 0) return []

  return weightedBadges
    .filter(Boolean)
    .sort((a, b) => a?.weight! - b?.weight!) as OrderedBadge[]
}
